import React, { useState, useEffect } from 'react';
import { z } from 'zod';
import { InvitationType } from '../../_graphql/generated/operations';
import { invitationTypesApi } from '../../_lib/api-client';
import type { CreateInvitationTypeInput } from '../../_graphql/generated/types';

const createInvitationTypeSchema = z.object({
  name: z.string().min(1, 'Invitation type name is required'),
  description: z.string().optional()
});

export type CreateInvitationTypeFormData = z.infer<typeof createInvitationTypeSchema>;

interface InvitationTypeFormProps {
  eventId?: string;
  initialData?: InvitationType;
  isOpen?: boolean;
  onSubmit?: (data: CreateInvitationTypeFormData) => void | Promise<void>;
  onSuccess?: (invitationType: InvitationType) => void;
  onCancel: () => void;
  existingTypes?: InvitationType[];
}

type State =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; error: string };

const InvitationTypeForm: React.FC<InvitationTypeFormProps> = ({ 
  eventId, 
  initialData,
  isOpen = true,
  onSubmit, 
  onSuccess,
  onCancel,
  existingTypes = []
}) => {
  const [formData, setFormData] = useState<CreateInvitationTypeFormData>({
    name: initialData?.name || '',
    description: initialData?.description || ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [state, setState] = useState<State>({ status: 'idle' });
  const [selectedCopyType, setSelectedCopyType] = useState<string>('');
  const [availableTypes, setAvailableTypes] = useState<InvitationType[]>(existingTypes);

  // Load available invitation types if not provided
  useEffect(() => {
    if (existingTypes.length === 0 && eventId && isOpen) {
      invitationTypesApi.getByEvent(eventId).then(types => {
        setAvailableTypes(types);
      }).catch(err => {
        console.error('Failed to load invitation types:', err);
      });
    } else {
      setAvailableTypes(existingTypes);
    }
  }, [eventId, existingTypes, isOpen]);

  // Handle copying from existing type
  useEffect(() => {
    if (selectedCopyType) {
      const typeToCopy = availableTypes.find(t => t.id === selectedCopyType);
      if (typeToCopy) {
        setFormData({
          name: `${typeToCopy.name} (Copy)`,
          description: typeToCopy.description || ''
        });
      }
    }
  }, [selectedCopyType, availableTypes]);

  const handleChange = (field: keyof CreateInvitationTypeFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
    setState({ status: 'idle' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const result = createInvitationTypeSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setState({ status: 'submitting' });

    try {
      // If custom onSubmit handler is provided, use it
      if (onSubmit) {
        await onSubmit(formData);
        setState({ status: 'idle' });
        return;
      }
      
      // Otherwise, use the API directly
      if (!eventId) {
        throw new Error('Event ID is required');
      }

      const input: CreateInvitationTypeInput = {
        eventId,
        name: formData.name,
        description: formData.description || undefined,
        designCustomizations: selectedCopyType ? 
          availableTypes.find(t => t.id === selectedCopyType)?.designCustomizations || undefined :
          undefined
      };

      const newInvitationType = await invitationTypesApi.create(input);
      
      if (onSuccess) {
        onSuccess(newInvitationType);
      }
      
      setState({ status: 'idle' });
    } catch (e) {
      setState({ 
        status: 'error', 
        error: e instanceof Error ? e.message : 'Failed to create invitation type' 
      });
    }
  };


  if (!isOpen) return null;

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-4">
          {/* Copy from existing type */}
          {availableTypes.length > 0 && (
            <div>
              <label htmlFor="copyFrom" className="block text-sm font-medium text-gray-700 mb-1">
                Copy from existing (optional)
              </label>
              <select
                id="copyFrom"
                value={selectedCopyType}
                onChange={(e) => setSelectedCopyType(e.target.value)}
                className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-100 focus:border-rose-400 bg-rose-50/30"
                disabled={state.status === 'submitting'}
              >
                <option value="">-- Select an invitation type to copy --</option>
                {availableTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={handleChange('name')}
              placeholder="e.g., Family & Friends, VIP, Colleagues"
              className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-100 focus:border-rose-400 bg-rose-50/30"
              disabled={state.status === 'submitting'}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={handleChange('description')}
              placeholder="Optional description for this invitation type"
              rows={3}
              className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-100 focus:border-rose-400 bg-rose-50/30 resize-none"
              disabled={state.status === 'submitting'}
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
          </div>

          {state.status === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{state.error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={state.status === 'submitting'}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-full hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {state.status === 'submitting' 
                ? (initialData ? 'Updating...' : 'Creating...') 
                : (initialData ? 'Update Type' : 'Create Type')
              }
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={state.status === 'submitting'}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
  );

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.15)', backdropFilter: 'blur(2px)' }}
    >
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border border-rose-100">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold bg-gradient-to-r from-rose-400 to-pink-600 bg-clip-text text-transparent">Add Invitation Type</h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
            disabled={state.status === 'submitting'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {formContent}
      </div>
    </div>
  );
};

export default InvitationTypeForm;